// ============================================================================
// frontend/src/pages/CreateTenant.tsx
// ============================================================================

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from 'pages/Page';
import { useAuth } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

// Hooks
import { useTenantForm } from '@/hooks/useTenantForm';
import { useTenantProvisioning } from '@/hooks/useTenantProvisioning';

// Components
import { BasicInfoSection } from '@/components/tenant/BasicInfoSection';
import { BrandingSection } from '@/components/tenant/BrandingSection';
import { ThemeSelector } from '@/components/tenant/ThemeSelector';
import { ThemeCustomizer } from '@/components/tenant/ThemeCustomizer';
import { ThemePreview } from '@/components/tenant/ThemePreview';
import { OAuthSection } from '@/components/tenant/OAuthSection';
import { AgentsSection } from '@/components/tenant/AgentsSection';
import { ProvisioningStatus } from '@/components/tenant/ProvisioningStatus';

const CreateTenantContent = () => {
  const { user, isAuthenticated, isReady } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);  // ← ICI
  const errorRef = useRef<HTMLDivElement>(null);       // ← ICI

  // Form state
  const {
    formData,
    setFormData,
    selectedTheme,
    customTheme,
    setCustomTheme,
    handleFileChange,
    applyTheme
  } = useTenantForm();

  // Provisioning state
  const {
    loading,
    error,
    elapsedTime,
    provisioningStatus,
    provisioningSteps,
    showForm,
    deployedUrl,
    handleSubmit,
    setError
  } = useTenantProvisioning();

  useEffect(() => {
    if (!isReady) return;
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isReady, navigate]);

  if (!isReady) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="!size-6" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Réinitialiser les erreurs précédentes
    setError(null);

    // ✅ Debug : Vérifier les agents sélectionnés
    console.log('🔍 Validation - Agents sélectionnés:', {
      count: formData.selected_agents.length,
      agents: formData.selected_agents,
      formData: formData
    });

    // Validation avec collecte des erreurs
    const validationErrors: string[] = [];

    if (!formData.name.trim()) {
      validationErrors.push('Le nom du cabinet est obligatoire');
    }

    if (!formData.subdomain.trim()) {
      validationErrors.push('Le sous-domaine est obligatoire');
    }

    if (!formData.admin_email.trim()) {
      validationErrors.push('L\'email administrateur est obligatoire');
    }

    if (!formData.admin_password) {
      validationErrors.push('Le mot de passe est obligatoire');
    }

    if (formData.admin_password !== formData.admin_password_confirm) {
      validationErrors.push('Les mots de passe ne correspondent pas');
    }

    if (formData.admin_password.length < 8) {
      validationErrors.push('Le mot de passe doit contenir au moins 8 caractères');
    }

    // ✅ Validation des agents - DÉPLACÉE ICI pour permettre le scroll
    if (formData.selected_agents.length === 0) {
      validationErrors.push('Veuillez sélectionner au moins un agent pour ce tenant');
    } else {
      // Vérifier qu'au moins un agent par défaut est sélectionné
      const hasDefaultAgent = formData.selected_agents.some(agent => agent.is_default);
      if (!hasDefaultAgent) {
        validationErrors.push('Vous devez définir un agent par défaut parmi les agents sélectionnés');
      }
    }

    // Si des erreurs existent, les afficher et scroller vers le haut
    if (validationErrors.length > 0) {
      // Créer un message d'erreur avec liste
      const errorTitle = validationErrors.length === 1 
        ? '⚠️ Erreur de validation :' 
        : `⚠️ ${validationErrors.length} erreurs de validation :`;
      
      const errorMessage = errorTitle + '\n\n' + validationErrors.map(err => `• ${err}`).join('\n');
      
      // Afficher l'erreur dans l'UI
      setError(errorMessage);
      
      // Scroller vers le haut après un court délai pour laisser le DOM se mettre à jour
      setTimeout(() => {
        // Tentative 1: Scroller le conteneur principal
        if (containerRef.current) {
          containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
        
        // Tentative 2: Scroller le message d'erreur dans la vue
        if (errorRef.current) {
          errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        
        // Tentative 3: Scroller window (au cas où)
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Tentative 4: Trouver tous les parents avec overflow et les scroller
        let parent = containerRef.current?.parentElement;
        while (parent) {
          if (parent.scrollTop > 0) {
            parent.scrollTo({ top: 0, behavior: 'smooth' });
          }
          parent = parent.parentElement;
        }
      }, 100);
      
      return; // Arrêter la soumission sans passer en état loading
    }

    // Si tout est valide, soumettre
    await handleSubmit(formData);
  };

  return (
    <div ref={containerRef} className="flex flex-col h-full w-full overflow-auto">
      <div className="max-w-4xl mx-auto w-full p-6">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à la liste
          </button>
          <h1 className="text-3xl font-bold mb-2">➕ Créer un nouveau tenant</h1>
          <p className="text-muted-foreground">
            Provisionnez un nouveau cabinet avec son instance dédiée et personnalisée
          </p>
        </div>

        {/* Provisioning Status */}
        <ProvisioningStatus
          status={provisioningStatus}
          steps={provisioningSteps}
          elapsedTime={elapsedTime}
          deployedUrl={deployedUrl}
        />

        {/* Error Message */}
        {error && (
          <div ref={errorRef} className="mb-6 rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive animate-shake">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Form */}
        <div className={`transition-all duration-500 ${showForm ? 'opacity-100 max-h-none' : 'opacity-0 max-h-0 overflow-hidden'}`}>
          {showForm && (
            <form onSubmit={onSubmit} className="space-y-6">
              {/* Section 1: Basic Info */}
              <BasicInfoSection formData={formData} setFormData={setFormData} />

              {/* Section 2: Branding */}
              <BrandingSection formData={formData} handleFileChange={handleFileChange} />

              {/* Section 3: Theme */}
              <div className="rounded-lg border bg-card p-6 space-y-6">
                <h2 className="text-xl font-semibold border-b pb-3">🎨 Thème & Apparence</h2>
                
                <ThemeSelector
                  selectedTheme={selectedTheme}
                  customTheme={customTheme}
                  onThemeSelect={applyTheme}
                  onCustomThemeToggle={(enabled) => {
                    setCustomTheme(enabled);
                    if (!enabled) {
                      applyTheme(selectedTheme);
                    }
                  }}
                />

                {customTheme && (
                  <ThemeCustomizer formData={formData} setFormData={setFormData} />
                )}
              </div>

              {/* Section 4: Preview */}
              <ThemePreview
                formData={formData}
                selectedTheme={selectedTheme}
                customTheme={customTheme}
              />

              {/* Section 5: OAuth */}
              <OAuthSection formData={formData} setFormData={setFormData} />
              {/* Section 6: Agents - NOUVEAU */}
              <AgentsSection formData={formData} setFormData={setFormData} />

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={loading || provisioningStatus === 'provisioning'}
                  className="inline-flex flex-1 items-center justify-center rounded-md bg-primary px-4 py-3 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 disabled:opacity-50"
                >
                  {loading || provisioningStatus === 'provisioning' ? (
                    <>
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                      {provisioningStatus === 'creating_tenant' && 'Création...'}
                      {provisioningStatus === 'provisioning' && 'Provisioning...'}
                      {!provisioningStatus && 'Enregistrement...'}
                    </>
                  ) : (
                    '🚀 Créer et déployer le tenant'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/admin/tenants')}
                  disabled={loading || provisioningStatus === 'provisioning'}
                  className="inline-flex items-center justify-center rounded-md border border-input bg-background px-6 py-3 text-sm font-medium"
                >
                  Annuler
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Animations CSS */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default function CreateTenant() {
  return (
    <Page>
      <CreateTenantContent />
    </Page>
  );
}